import {effect, Injectable, Signal, signal, WritableSignal} from '@angular/core';
import {toObservable} from '@angular/core/rxjs-interop';
import {UCService, UCServiceCategory} from '../../interfaces/user-centrics.interface';

export const GOOGLE_TAG_MANAGER = 'Google Tag Manager';
export const WALK_ME = 'WalkMe';
export const DATADOG = 'Datadog';

@Injectable({
  providedIn: 'root',
})
export class UserCentricsService {
  initialized = signal<boolean>(false);
  services = signal<UCService[]>([]);
  hasConsentByService = {} as Record<string, WritableSignal<boolean>>;

  constructor() {
    toObservable(this.initialized).subscribe((initialized) => {
      if (initialized) {
        this.services.set(this.initServices());
      } else {
        this.services.set([]);
      }
    });
    this.listenToConsentChanges();
    effect(() => {
      this.services().map((service) => {
        [service.id, service.name].map((key) => {
          if (this.hasConsentByService[key]) {
            this.hasConsentByService[key].set(service.consent);
          } else {
            this.hasConsentByService[key] = signal(service.consent);
          }
        });
      });
    });
  }

  hasConsent(service: string): Signal<boolean> {
    if (!this.hasConsentByService[service]) {
      this.hasConsentByService[service] = signal(false);
    }
    return this.hasConsentByService[service];
  }

  private initServices(): UCService[] {
    const services = ((window as any)?.UC_UI?.getServicesBaseInfo?.() ?? []) as any[];
    return services.map(
      (service) =>
        ({
          id: service.id,
          name: service.name,
          category: service.categorySlug as UCServiceCategory,
          consent: service.consent.status,
        }) as UCService,
    );
  }

  private listenToConsentChanges(): void {
    (window as any).addEventListener('ucEvent', (e: CustomEvent) => {
      if (e.detail?.event === 'consent_status') {
        const updated = this.services().map((service) => {
          // Use the service name as the lookup key in e.detail
          const consent = e.detail.hasOwnProperty(service.name)
            ? e.detail[service.name]
            : service.consent;

          if (
            [GOOGLE_TAG_MANAGER, WALK_ME].includes(service.name) &&
            service.consent &&
            consent === false
          ) {
            // If consent is being revoked for Google Tag Manager or WalkMe, reload the page!
            // TODO: can be removed once these services support dynamic disabling
            console.warn(
              `Consent revoked for ${service.name}. Reloading the page to ensure scripts are disabled.`,
            );
            window.location.reload();
          }

          return {
            ...service,
            consent: consent,
          };
        });
        this.services.set(updated);
      }
    });
  }
}
